/**
 * F-508-FE-11: SpecializationManagement
 *
 * /admin/specializations
 * - List: name, short code, description, vocabulary status, status badge
 * - Clicking a row navigates to /admin/specializations/:id (detail page)
 * - Create: form with name, short code (2-8 chars uppercase alphanumeric), description
 * - Edit: short code disabled after first doctor registers (tooltip). Name + description editable.
 * - Activate/deactivate (last active disabled)
 * - Vocabulary management moved to SpecializationDetailPage (VocabularyTab)
 *
 * API:
 * - GET /admin/specializations
 * - POST /admin/specializations
 * - PATCH /admin/specializations/:id
 * - POST /admin/specializations/:id/activate
 * - POST /admin/specializations/:id/deactivate
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/layouts/AdminLayout';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Specialization {
  id: string;
  name: string;
  shortCode: string;
  description: string;
  status: 'ACTIVE' | 'INACTIVE';
  isCodeImmutable: boolean; // true after first doctor registers
  currentVocabularyVersionId: string | null;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const SHORT_CODE_PATTERN = /^[A-Z0-9]{2,8}$/;

function validateShortCode(code: string): string | null {
  if (!code) return 'Short code is required.';
  if (!SHORT_CODE_PATTERN.test(code)) {
    return '2–8 uppercase alphanumeric characters only (A-Z, 0-9).';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: 'ACTIVE' | 'INACTIVE' }) {
  if (status === 'ACTIVE') {
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200">Active</span>;
  }
  return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">Inactive</span>;
}

// ---------------------------------------------------------------------------
// Vocabulary status indicator
// ---------------------------------------------------------------------------

function VocabularyStatusChip({ hasVocabulary }: { hasVocabulary: boolean }) {
  if (hasVocabulary) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200">
        <span className="w-1.5 h-1.5 rounded-full bg-teal-500 flex-none" />
        Active
      </span>
    );
  }
  return (
    <span className="text-xs font-body text-clinical-text-muted">None</span>
  );
}

// ---------------------------------------------------------------------------
// Specialization row (click-to-navigate, no expand)
// ---------------------------------------------------------------------------

interface SpecializationRowProps {
  spec: Specialization;
  isOnlyActive: boolean;
  onEdit: (e: React.MouseEvent) => void;
  onActivate: (e: React.MouseEvent) => void;
  onDeactivate: (e: React.MouseEvent) => void;
  isActivating: boolean;
  isDeactivating: boolean;
}

function SpecializationRow({
  spec,
  isOnlyActive,
  onEdit,
  onActivate,
  onDeactivate,
  isActivating,
  isDeactivating,
}: SpecializationRowProps) {
  const navigate = useNavigate();

  return (
    <tr
      className="hover:bg-clinical-cream/50 transition-colors cursor-pointer"
      onClick={() => navigate(`/admin/specializations/${spec.id}`)}
    >
      <td className="px-5 py-3">
        <span className="font-medium text-clinical-charcoal hover:text-clinical-teal transition-colors">
          {spec.name}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="font-mono text-xs bg-clinical-cream-dark text-clinical-charcoal px-2 py-0.5 rounded">
          {spec.shortCode}
        </span>
      </td>
      <td className="px-4 py-3 text-clinical-text-muted hidden md:table-cell max-w-xs truncate">
        {spec.description || '—'}
      </td>
      <td className="px-4 py-3">
        <VocabularyStatusChip hasVocabulary={Boolean(spec.currentVocabularyVersionId)} />
      </td>
      <td className="px-4 py-3"><StatusBadge status={spec.status} /></td>
      <td className="px-5 py-3">
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(e); }}
            className="text-xs text-clinical-text-muted font-medium hover:text-clinical-charcoal"
          >
            Edit
          </button>
          <span className="text-clinical-border">|</span>
          {spec.status === 'ACTIVE' ? (
            <span title={isOnlyActive ? 'Cannot deactivate the last active specialization.' : undefined}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); if (!isOnlyActive) onDeactivate(e); }}
                disabled={isOnlyActive || isDeactivating}
                className="text-xs text-red-500 font-medium hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Deactivate
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onActivate(e); }}
              disabled={isActivating}
              className="text-xs text-clinical-teal font-medium hover:underline"
            >
              Activate
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Form modal
// ---------------------------------------------------------------------------

interface FormModalProps {
  open: boolean;
  specialization: Specialization | null;
  onClose: () => void;
  onSuccess: () => void;
}

function FormModal({ open, specialization, onClose, onSuccess }: FormModalProps) {
  const isEdit = specialization !== null;

  const [name, setName] = useState(specialization?.name ?? '');
  const [shortCode, setShortCode] = useState(specialization?.shortCode ?? '');
  const [description, setDescription] = useState(specialization?.description ?? '');
  const [shortCodeError, setShortCodeError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleShortCodeChange = (val: string) => {
    const upper = val.toUpperCase();
    setShortCode(upper);
    const err = validateShortCode(upper);
    setShortCodeError(err ?? '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEdit) {
      const codeErr = validateShortCode(shortCode);
      if (codeErr) {
        setShortCodeError(codeErr);
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      if (isEdit && specialization) {
        await api.patch(`/admin/specializations/${specialization.id}`, {
          name: name.trim(),
          description: description.trim(),
        });
      } else {
        await api.post('/admin/specializations', {
          name: name.trim(),
          shortCode: shortCode.trim(),
          description: description.trim(),
        });
      }
      onSuccess();
      onClose();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save specialization.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-clinical-charcoal/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-clinical-lg max-w-md w-full p-6">
        <h3 className="font-heading text-lg font-semibold text-clinical-charcoal mb-5">
          {isEdit ? `Edit ${specialization.name}` : 'Create Specialization'}
        </h3>
        <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
          <div>
            <label htmlFor="spec-name" className="form-label">Name</label>
            <input
              id="spec-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="form-input"
              placeholder="e.g. Obstetrics & Gynecology"
              required
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="spec-code" className="form-label">
              Short Code
              {isEdit && specialization?.isCodeImmutable && (
                <span className="ms-2 text-xs font-normal text-clinical-text-muted">(locked after first doctor)</span>
              )}
            </label>
            <input
              id="spec-code"
              type="text"
              value={shortCode}
              onChange={(e) => handleShortCodeChange(e.target.value)}
              className={`form-input font-mono uppercase ${isEdit && specialization?.isCodeImmutable ? 'bg-clinical-cream-dark text-clinical-text-muted cursor-not-allowed' : ''} ${shortCodeError ? 'border-red-400 focus:border-red-500 focus:ring-red-300' : ''}`}
              placeholder="e.g. OBGYN"
              required={!isEdit}
              disabled={isEdit && specialization?.isCodeImmutable}
              title={isEdit && specialization?.isCodeImmutable ? 'Short code cannot be changed after a doctor has registered with this specialization.' : undefined}
              maxLength={8}
            />
            {shortCodeError ? (
              <p className="text-xs font-body text-red-600 mt-1">{shortCodeError}</p>
            ) : !isEdit ? (
              <p className="text-xs font-body text-clinical-text-muted mt-1">
                2–8 uppercase letters/numbers. Cannot be changed after first doctor registers.
              </p>
            ) : specialization?.isCodeImmutable ? (
              <p className="text-xs font-body text-clinical-text-muted mt-1">
                Short code is locked to prevent session data integrity issues.
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor="spec-desc" className="form-label">Description</label>
            <textarea
              id="spec-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="form-textarea"
              placeholder="Brief description of this specialization…"
              rows={3}
            />
          </div>

          {submitError && (
            <div className="rounded-clinical border border-red-200 bg-red-50 px-4 py-3">
              <p className="text-sm font-body text-red-700">{submitError}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 text-sm min-h-[36px]">Cancel</button>
            <button
              type="submit"
              disabled={isSubmitting || (!isEdit && Boolean(shortCodeError))}
              className="btn-primary px-4 py-2 text-sm min-h-[36px]"
            >
              {isSubmitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SpecializationManagement() {
  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState(false);
  const [editSpec, setEditSpec] = useState<Specialization | null>(null);

  const { data: specializations, isLoading } = useQuery<Specialization[]>({
    queryKey: ['admin', 'specializations'],
    queryFn: async () => {
      const res = await api.get<{ data: Specialization[]; count: number }>('/admin/specializations');
      return res.data;
    },
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['admin', 'specializations'] });

  const activateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/admin/specializations/${id}/activate`),
    onSuccess: invalidate,
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/admin/specializations/${id}/deactivate`),
    onSuccess: invalidate,
  });

  const activeCount = (specializations?.filter((s) => s.status === 'ACTIVE') ?? []).length;
  const isLastActive = activeCount === 1;

  return (
    <AdminLayout breadcrumbs={[{ label: 'Specializations' }]}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-clinical-charcoal">Specializations</h1>
          <p className="text-sm font-body text-clinical-text-muted mt-1">
            Manage clinical specializations. Click a row to view details and manage vocabulary.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setEditSpec(null); setShowModal(true); }}
          className="btn-primary text-sm px-4 py-2 min-h-[40px]"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          New Specialization
        </button>
      </div>

      {/* Table */}
      <div className="clinical-card overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="skeleton h-12 w-full rounded" />
            ))}
          </div>
        ) : (specializations?.length ?? 0) === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm font-body text-clinical-text-muted">No specializations configured yet.</p>
            <button
              type="button"
              onClick={() => { setEditSpec(null); setShowModal(true); }}
              className="btn-primary text-sm px-4 py-2 min-h-[40px] mt-4"
            >
              Create First Specialization
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="border-b border-clinical-border bg-clinical-cream">
                  <th className="text-start px-5 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">Name</th>
                  <th className="text-start px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">Code</th>
                  <th className="text-start px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide hidden md:table-cell">Description</th>
                  <th className="text-start px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">Vocabulary</th>
                  <th className="text-start px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">Status</th>
                  <th className="text-end px-5 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-clinical-border">
                {specializations?.map((spec) => {
                  const isOnlyActive = spec.status === 'ACTIVE' && isLastActive;
                  return (
                    <SpecializationRow
                      key={spec.id}
                      spec={spec}
                      isOnlyActive={isOnlyActive}
                      onEdit={(e) => { e.stopPropagation(); setEditSpec(spec); setShowModal(true); }}
                      onActivate={(e) => { e.stopPropagation(); activateMutation.mutate(spec.id); }}
                      onDeactivate={(e) => { e.stopPropagation(); deactivateMutation.mutate(spec.id); }}
                      isActivating={activateMutation.isPending}
                      isDeactivating={deactivateMutation.isPending}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <FormModal
        open={showModal}
        specialization={editSpec}
        onClose={() => setShowModal(false)}
        onSuccess={invalidate}
      />
    </AdminLayout>
  );
}
